/**
 * @file FeedbackPage.tsx
 * @brief Super Admin → Feedback. Read-only browse of session + app feedback.
 *
 * @details
 * Two segmented views over the feedback tables:
 *   - Session feedback: per-session ratings from the shared FeedbackSheet
 *     (conversation | realtime | meetingintel | medical), with type +
 *     min-rating filters.
 *   - App feedback: periodic overall-app ratings (review-gating source).
 *
 * @dependencies
 * - features/feedback/api (TanStack Query hooks)
 * - components/data/DataTable, components/ui/{Badge,HeaderFilterMenu,DateRangePicker}
 *
 * @references
 * - BACKEND/src/features/feedback/routes.js
 *
 * @author DES
 * @date 05-07-2026
 * @updated 01-10-2026: surface / rating became header pills and the dates
 *          the header's calendar circle; the filter state lives on the page
 *          (one set per tab) so the header can hold them
 * @updated 01-10-2026: user groups come from the server (`?group=user`)
 *          with real counts; a group fetches its own rows when opened
 *          (`?user=`), 25 at a time. Open groups live in the URL.
 */

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Star } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/data/DataTable";
import type { TableRowGroup } from "@/components/data/groupRowsBy";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { formatDateTime } from "@/shared/utils/formatters";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { cn } from "@/shared/theme/cn";
import { feedbackUserGroupMeta } from "@/shared/utils/tableGroups";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import {
  feedbackUserKey,
  useFeedbackGroupRows,
  useFeedbackGroupSummary,
} from "./api";
import type {
  AppFeedback,
  AppFeedbackListParams,
  FeedbackSessionType,
  SessionFeedback,
  SessionFeedbackListParams,
} from "./schemas";
const ALL = "__all__";

const SURFACE_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any surface" },
  { value: "conversation", label: "Conversation" },
  { value: "realtime", label: "Realtime" },
  { value: "meetingintel", label: "Meeting Intel" },
  { value: "medical", label: "Medical" },
];

const RATING_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any rating" },
  { value: "4", label: "4★ and up" },
  { value: "3", label: "3★ and up" },
  { value: "2", label: "2★ and up" },
  { value: "1", label: "1★ and up" },
];

const labelOf = (items: SearchableMenuItem[], v: string) =>
  items.find((i) => i.value === v)?.label ?? items[0].label;

type DateRange = { from?: string; to?: string };

type SessionFeedbackFilters = DateRange & {
  type?: FeedbackSessionType;
  minRating?: number;
};

const TYPE_LABEL: Record<FeedbackSessionType, string> = {
  conversation: "Conversation",
  realtime: "Realtime",
  meetingintel: "Meeting Intel",
  medical: "Medical",
};

// Human labels for the issue tag keys the app sends (see FeedbackSheet).
const TAG_LABEL: Record<string, string> = {
  wrong_translation: "Wrong translation",
  slow_response: "Too slow",
  audio_quality: "Audio quality",
  wrong_language: "Wrong language",
  app_glitch: "App glitch",
  missing_transcript: "Missing transcript",
  wrong_speaker: "Wrong speaker",
  summary_quality: "Summary quality",
  // Medical assessment (19-09-2026).
  missing_detail: "Missed something",
  report_accuracy: "Wrong in the report",
  transcript_quality: "Transcript errors",
};

function tagLabel(key: string): string {
  return (
    TAG_LABEL[key] ??
    key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
  );
}

// ----------------------------------------------------------------------
// Small star-rating renderer (1-5)
// ----------------------------------------------------------------------
function StarRating({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5" title={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={
            n <= value
              ? "h-3.5 w-3.5 fill-warning text-warning"
              : "h-3.5 w-3.5 text-muted-foreground/40"
          }
        />
      ))}
    </div>
  );
}

function TagChips({ tags, limit }: { tags: string[]; limit?: number }) {
  if (!tags?.length) return <span className="text-muted-foreground">—</span>;
  const shown = limit ? tags.slice(0, limit) : tags;
  const overflow = limit ? tags.length - shown.length : 0;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((t) => (
        <Badge key={t} variant="muted" className="whitespace-nowrap text-[11px]">
          {tagLabel(t)}
        </Badge>
      ))}
      {overflow > 0 ? (
        <span
          className="text-xs text-muted-foreground"
          title={tags.map(tagLabel).join(", ")}
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  );
}

function CommentCell({ comment }: { comment?: string | null }) {
  if (!comment) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="line-clamp-2 max-w-[280px] text-sm" title={comment}>
      {comment}
    </span>
  );
}

/** The bare field's value line, for a read-only value (17px medium, as
 *  `bareControlClass`), so the detail reads like the forms around it. */
const readOnlyValueClass = "min-h-6 text-[17px] font-medium leading-[21px] text-app-text";

function FeedbackDetailDialog({
  feedback,
  onOpenChange,
}: {
  feedback: (SessionFeedback | AppFeedback) | null;
  onOpenChange: (open: boolean) => void;
}) {
  const isSession = feedback != null && "session_type" in feedback;
  const s = isSession ? (feedback as SessionFeedback) : null;
  const a = feedback != null && !isSession ? (feedback as AppFeedback) : null;
  const name = feedback
    ? [feedback.user_first_name, feedback.user_last_name].filter(Boolean).join(" ").trim()
    : "";
  // Meta row: one column per value shown, so it never leaves a gap.
  const metaCount =
    2 + (feedback?.app_version ? 1 : 0) + (a && a.routed_to_store ? 1 : 0);

  return (
    <FormSheet open={feedback != null} onOpenChange={onOpenChange}>
      {/* DES 01-10-2026: form standard. Cmd+K palette frame; read-only
          values as bare fields in columns (rating, surface/NPS and user in
          one row, the meta in another); short, so no tabs. */}
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>Feedback detail</FormSheetTitle>
            <FormSheetDescription>
              Read-only rating and comment from the mobile app.
            </FormSheetDescription>
          </FormSheetHeader>

          <FormSheetBody>
            {feedback ? (
              <>
                <FormSheetSection title="About">
                  <FormSheetFields className="sm:grid-cols-3">
                    <FormSheetField>
                      {/* Every field in this row carries a hint, so the
                          hairlines line up (DES 01-10-2026: form standard). */}
                      <FormFieldGroup
                        label="Rating"
                        hint={`${feedback.overall_rating} out of 5.`}
                      >
                        <div className="flex min-h-6 items-center">
                          <StarRating value={feedback.overall_rating} />
                        </div>
                      </FormFieldGroup>
                    </FormSheetField>
                    {s ? (
                      <FormSheetField>
                        <FormFieldGroup label="Surface" hint="Where it was rated.">
                          <div className="flex min-h-6 items-center">
                            <Badge variant="info">{TYPE_LABEL[s.session_type]}</Badge>
                          </div>
                        </FormFieldGroup>
                      </FormSheetField>
                    ) : null}
                    {a ? (
                      <FormSheetField>
                        <FormFieldGroup label="NPS" hint="Would recommend, out of 10.">
                          <p className={cn(readOnlyValueClass, "tabular-nums")}>
                            {a.would_recommend == null
                              ? "Not given"
                              : `${a.would_recommend} / 10`}
                          </p>
                        </FormFieldGroup>
                      </FormSheetField>
                    ) : null}
                    <FormSheetField>
                      <FormFieldGroup
                        label="User"
                        hint={
                          name && feedback.user_email
                            ? feedback.user_email
                            : "No name on the account."
                        }
                      >
                        <p className={cn(readOnlyValueClass, "truncate")}>
                          {name || feedback.user_email || "Unknown"}
                        </p>
                      </FormFieldGroup>
                    </FormSheetField>
                    {s && s.tags.length > 0 ? (
                      <FormSheetField className="sm:col-span-3">
                        <FormFieldGroup label="Issues">
                          <TagChips tags={s.tags} />
                        </FormFieldGroup>
                      </FormSheetField>
                    ) : null}
                  </FormSheetFields>
                </FormSheetSection>

                <FormSheetSection title="Comment">
                  <FormSheetFields className="sm:grid-cols-1">
                    <FormSheetField span={2}>
                      {feedback.comment ? (
                        <p className="whitespace-pre-wrap break-words text-sm">
                          {feedback.comment}
                        </p>
                      ) : (
                        <span className="text-sm text-muted-foreground">No comment.</span>
                      )}
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>

                <FormSheetSection title="Meta">
                  <FormSheetFields
                    className={cn(
                      metaCount >= 4
                        ? "sm:grid-cols-4"
                        : metaCount === 3
                          ? "sm:grid-cols-3"
                          : "sm:grid-cols-2",
                    )}
                  >
                    <FormSheetField>
                      <FormFieldGroup label="Platform">
                        <p className={cn(readOnlyValueClass, "capitalize")}>
                          {feedback.platform ?? "Unknown"}
                        </p>
                      </FormFieldGroup>
                    </FormSheetField>
                    {feedback.app_version ? (
                      <FormSheetField>
                        <FormFieldGroup label="App version">
                          <p className={readOnlyValueClass}>{feedback.app_version}</p>
                        </FormFieldGroup>
                      </FormSheetField>
                    ) : null}
                    <FormSheetField>
                      <FormFieldGroup label="When">
                        <p className={readOnlyValueClass}>
                          {formatDateTime(feedback.created_at)}
                        </p>
                      </FormFieldGroup>
                    </FormSheetField>
                    {a && a.routed_to_store ? (
                      <FormSheetField>
                        <FormFieldGroup label="Routed">
                          <div className="flex min-h-6 items-center">
                            <Badge variant="success" className="capitalize">
                              {a.external_platform ?? "store"}
                            </Badge>
                          </div>
                        </FormFieldGroup>
                      </FormSheetField>
                    ) : null}
                    {s ? (
                      <FormSheetField className="col-span-full">
                        <FormFieldGroup label="Session">
                          <p className="break-all font-mono text-xs text-muted-foreground">
                            {s.session_id}
                          </p>
                        </FormFieldGroup>
                      </FormSheetField>
                    ) : null}
                  </FormSheetFields>
                </FormSheetSection>
              </>
            ) : null}
          </FormSheetBody>

          <FormSheetFooter>
            <FormSheetFooterCell>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className={formSheetActionClass}
              >
                Close
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}

// ----------------------------------------------------------------------
// Server-backed user groups (DES 01-10-2026: "haan superadmin pages pe bhi
// yehi kar do"). Every user with feedback under the filters comes from the
// server with a real count, not just the users on one page; a group fetches
// its own rows when opened, 25 at a time. Open groups live in the URL
// (`<param>=key:batches,...`, one param per tab); a search opens them all.
// ----------------------------------------------------------------------
function useFeedbackUserGroups<K extends "session" | "app">(
  kind: K,
  params: K extends "session" ? SessionFeedbackListParams : AppFeedbackListParams,
  openParamName: string,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const summary = useFeedbackGroupSummary(kind, params, true);
  const openParam = searchParams.get(openParamName) ?? "";
  const openGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const part of openParam.split(",")) {
      const [key, n] = part.split(":");
      if (key) map.set(key, Math.max(1, Number(n) || 1));
    }
    return map;
  }, [openParam]);
  const setOpenGroups = (update: (prev: Map<string, number>) => Map<string, number>) => {
    const next = update(openGroups);
    const usp = new URLSearchParams(searchParams);
    const value = [...next].map(([k, n]) => (n > 1 ? `${k}:${n}` : k)).join(",");
    if (value) usp.set(openParamName, value);
    else usp.delete(openParamName);
    setSearchParams(usp, { replace: true });
  };
  const searching = Boolean(params.search?.trim());
  const openList = useMemo(() => {
    const keys = searching
      ? (summary.data ?? []).map((g) => feedbackUserKey(g.user_id))
      : [...openGroups.keys()];
    return keys.map((key) => ({ key, batches: openGroups.get(key) ?? 1 }));
  }, [searching, summary.data, openGroups]);
  const groupRows = useFeedbackGroupRows(kind, params, openList);
  type Row = K extends "session" ? SessionFeedback : AppFeedback;
  const groups = useMemo<TableRowGroup<Row>[] | undefined>(() => {
    if (!summary.data?.length) return undefined;
    return summary.data
      .map((g) => {
        const key = feedbackUserKey(g.user_id);
        const idx = openList.findIndex((o) => o.key === key);
        const q = idx >= 0 ? groupRows[idx] : undefined;
        return {
          key,
          ...feedbackUserGroupMeta(g.first_name, g.last_name, g.email),
          rows: (q?.data?.items ?? []) as Row[],
          total: g.count,
          loading: Boolean(q?.isFetching),
          error: Boolean(q?.isError),
          onRetry: () => void q?.refetch(),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [summary.data, openList, groupRows]);
  const rows = useMemo(() => (groups ?? []).flatMap((g) => g.rows), [groups]);

  return {
    groups,
    rows,
    searching,
    summary,
    tableProps: {
      expandAllGroups: searching,
      expandedGroupKeys: [...openGroups.keys()],
      onGroupExpandChange: (key: string, expanded: boolean) =>
        setOpenGroups((prev) => {
          const next = new Map(prev);
          if (expanded) next.set(key, next.get(key) ?? 1);
          else next.delete(key);
          return next;
        }),
      onGroupLoadMore: (key: string) =>
        setOpenGroups((prev) => new Map(prev).set(key, (prev.get(key) ?? 1) + 1)),
      isLoading: summary.isLoading,
      isFetching: summary.isFetching,
      error: summary.error,
      onRetry: () => summary.refetch(),
    },
  };
}

// ----------------------------------------------------------------------
// Session feedback view
// ----------------------------------------------------------------------
function SessionFeedbackView({
  filters,
  searchInput,
}: {
  filters: SessionFeedbackFilters;
  /** The page dock's search text (lifted so the dock can own the box). */
  searchInput: string;
}) {
  const { type, minRating } = filters;
  const debouncedSearch = useDebouncedValue(searchInput, 350);
  const [selected, setSelected] = useState<SessionFeedback | null>(null);

  const params: SessionFeedbackListParams = {
    type,
    minRating,
    from: filters.from,
    to: filters.to,
    search: debouncedSearch || undefined,
  };
  const { groups, rows, tableProps } = useFeedbackUserGroups("session", params, "open");

  const columns = useMemo<ColumnDef<SessionFeedback>[]>(
    () => [
      {
        header: "Rating",
        accessorKey: "overall_rating",
        cell: ({ row }) => <StarRating value={row.original.overall_rating} />,
      },
      {
        header: "Surface",
        accessorKey: "session_type",
        cell: ({ row }) => (
          <Badge variant="info">{TYPE_LABEL[row.original.session_type]}</Badge>
        ),
      },
      {
        header: "Issues",
        accessorKey: "tags",
        cell: ({ row }) => <TagChips tags={row.original.tags} limit={2} />,
      },
      {
        header: "Comment",
        accessorKey: "comment",
        cell: ({ row }) => <CommentCell comment={row.original.comment} />,
      },
      {
        header: "Session",
        accessorKey: "session_id",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.session_id.slice(0, 12)}…
          </span>
        ),
      },
      {
        header: "Platform",
        accessorKey: "platform",
        cell: ({ row }) => (
          <span className="text-sm capitalize">
            {row.original.platform ?? "—"}
          </span>
        ),
      },
      {
        header: "When",
        accessorKey: "created_at",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <GridCell padded={false} className="border-b-0">
        <DataTable<SessionFeedback, unknown>
          variant="cell"
          columns={columns}
          data={rows}
          groups={groups}
          {...tableProps}
          groupItemLabel="feedback"
          emptyTitle="No feedback yet"
          emptyDescription="Session feedback will appear here as users rate their sessions."
          onRowClick={(row) => setSelected(row)}
          rowKey={(row) => row.id}
        />
      </GridCell>

      <FeedbackDetailDialog
        feedback={selected}
        onOpenChange={(open) => !open && setSelected(null)}
      />
    </>
  );
}

// ----------------------------------------------------------------------
// App feedback view
// ----------------------------------------------------------------------
function AppFeedbackView({
  range,
  searchInput,
}: {
  range: DateRange;
  /** The page dock's search text (lifted so the dock can own the box). */
  searchInput: string;
}) {
  const debouncedSearch = useDebouncedValue(searchInput, 350);
  const [selected, setSelected] = useState<AppFeedback | null>(null);
  const params: AppFeedbackListParams = {
    from: range.from,
    to: range.to,
    search: debouncedSearch || undefined,
  };
  const { groups, rows, tableProps } = useFeedbackUserGroups("app", params, "appOpen");

  const columns = useMemo<ColumnDef<AppFeedback>[]>(
    () => [
      {
        header: "Rating",
        accessorKey: "overall_rating",
        cell: ({ row }) => <StarRating value={row.original.overall_rating} />,
      },
      {
        header: "NPS",
        accessorKey: "would_recommend",
        cell: ({ row }) =>
          row.original.would_recommend == null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="text-sm">{row.original.would_recommend} / 10</span>
          ),
      },
      {
        header: "Comment",
        accessorKey: "comment",
        cell: ({ row }) => <CommentCell comment={row.original.comment} />,
      },
      {
        header: "Routed",
        accessorKey: "routed_to_store",
        cell: ({ row }) =>
          row.original.routed_to_store ? (
            <Badge variant="success" className="capitalize">
              {row.original.external_platform ?? "store"}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        header: "Platform",
        accessorKey: "platform",
        cell: ({ row }) => (
          <span className="text-sm capitalize">
            {row.original.platform ?? "—"}
          </span>
        ),
      },
      {
        header: "When",
        accessorKey: "created_at",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <GridCell padded={false} className="border-b-0">
        <DataTable<AppFeedback, unknown>
          variant="cell"
          columns={columns}
          data={rows}
          groups={groups}
          {...tableProps}
          groupItemLabel="feedback"
          emptyTitle="No app feedback yet"
          emptyDescription="Periodic overall-app ratings will appear here."
          onRowClick={(row) => setSelected(row)}
          rowKey={(row) => row.id}
        />
      </GridCell>

      <FeedbackDetailDialog
        feedback={selected}
        onOpenChange={(open) => !open && setSelected(null)}
      />
    </>
  );
}

// ----------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------
type Tab = "session" | "app";

export function FeedbackPage() {
  const [tab, setTab] = useState<Tab>("session");
  // Each tab keeps its own filters; they sit in the header (01-10-2026).
  const [sessionFilters, setSessionFilters] = useState<SessionFeedbackFilters>({});
  const [appRange, setAppRange] = useState<DateRange>({});
  // Each tab's list search, typed in the bottom dock (DES 01-10-2026); the
  // views debounce it as before.
  const [sessionSearch, setSessionSearch] = useState("");
  const [appSearch, setAppSearch] = useState("");
  const surface = sessionFilters.type ?? ALL;
  const rating = sessionFilters.minRating ? String(sessionFilters.minRating) : ALL;

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Feedback</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Ratings and comments users leave after sessions, plus periodic
              app-level feedback.
            </p>
          </div>
          {/* The open tab's filters, top right (DES 01-10-2026): text pills,
              then the date circle. */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            {tab === "session" ? (
              <>
                <HeaderFilterMenu
                  title="Filter by surface"
                  searchPlaceholder="Search surfaces"
                  items={SURFACE_ITEMS}
                  selected={surface}
                  label={labelOf(SURFACE_ITEMS, surface)}
                  active={surface !== ALL}
                  onClear={() => setSessionFilters((f) => ({ ...f, type: undefined }))}
                  onSelect={(v) =>
                    setSessionFilters((f) => ({
                      ...f,
                      type: v === ALL ? undefined : (v as FeedbackSessionType),
                    }))
                  }
                />
                <HeaderFilterMenu
                  title="Filter by rating"
                  searchPlaceholder="Search ratings"
                  items={RATING_ITEMS}
                  selected={rating}
                  label={labelOf(RATING_ITEMS, rating)}
                  active={rating !== ALL}
                  onClear={() => setSessionFilters((f) => ({ ...f, minRating: undefined }))}
                  onSelect={(v) =>
                    setSessionFilters((f) => ({
                      ...f,
                      minRating: v === ALL ? undefined : Number(v),
                    }))
                  }
                />
                <DateRangePicker
                  variant="circle"
                  from={sessionFilters.from}
                  to={sessionFilters.to}
                  onChange={({ from, to }) => setSessionFilters((f) => ({ ...f, from, to }))}
                />
              </>
            ) : (
              <DateRangePicker
                variant="circle"
                from={appRange.from}
                to={appRange.to}
                onChange={({ from, to }) => setAppRange({ from, to })}
              />
            )}
          </div>
        </GridBand>

        <GridCells className="grid-cols-2">
          <GridCell padded={false}>
            <button
              type="button"
              onClick={() => setTab("session")}
              className={cn(
                "flex w-full items-center justify-center px-4 py-3 text-sm font-medium transition-colors",
                tab === "session"
                  ? "bg-brand-soft text-brand-soft-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              Session feedback
            </button>
          </GridCell>
          <GridCell padded={false}>
            <button
              type="button"
              onClick={() => setTab("app")}
              className={cn(
                "flex w-full items-center justify-center px-4 py-3 text-sm font-medium transition-colors",
                tab === "app"
                  ? "bg-brand-soft text-brand-soft-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              App feedback
            </button>
          </GridCell>
        </GridCells>

        {tab === "session" ? (
          <SessionFeedbackView filters={sessionFilters} searchInput={sessionSearch} />
        ) : (
          <AppFeedbackView range={appRange} searchInput={appSearch} />
        )}
      </GridBlock>

      {/* The Notes screen's search (DES 01-10-2026), docked at the bottom
          like Notes: the open tab's list search. Keyed per tab so each
          opens and closes on its own text. */}
      <PageDock>
        {tab === "session" ? (
          <ExpandingSearch
            key="session"
            value={sessionSearch}
            onChange={setSessionSearch}
            placeholder="Session ID, user, or comment"
            label="Search session feedback"
          />
        ) : (
          <ExpandingSearch
            key="app"
            value={appSearch}
            onChange={setAppSearch}
            placeholder="User or comment"
            label="Search app feedback"
          />
        )}
      </PageDock>
    </div>
  );
}

export default FeedbackPage;
